/**
 * React views for the GenUI widgets the Kaltura agent pushes over
 * `brainSegment` events.
 *
 * The SDK ships a parser + model builders but no React components:
 * `DEFAULT_RENDERERS[runtime](model, ctx)` turns the wire payload into a
 * descriptive model (`{ kind, data }`), and this module renders it:
 *
 * - "media" widgets (`video-gallery`, `external-video`, `content-gallery`)
 *   take over the session content area — the closest thing Kaltura has to
 *   Tavus's slide share (the agent decides when to push them).
 * - "aux" widgets (`sources`, `followups`, `show-link`) overlay the avatar
 *   video as compact cards.
 *
 * Data shapes are the SDK's own (see `genui/renderers/index.js`); everything
 * here is defensive — missing fields collapse the widget to nothing rather
 * than crash the session.
 */
import { useState } from "react";
import { ExternalLink, Film, Link2, Search } from "lucide-react";
import { cn } from "@/lib/utils";

export type GenUiWidget = { kind: string; data: Record<string, unknown> };

export const MEDIA_KINDS = new Set(["video-gallery", "external-video", "content-gallery"]);
export const AUX_KINDS = new Set(["sources", "followups", "show-link"]);

export function isMediaKind(kind: string): boolean {
  return MEDIA_KINDS.has(kind);
}
export function isAuxKind(kind: string): boolean {
  return AUX_KINDS.has(kind);
}

type GenUiLink = { title?: string; url?: string; snippet?: string };
type GenUiVideo = {
  title?: string;
  description?: string;
  url?: string;
  embedUrl?: string;
  poster?: string;
};
type GenUiItem = {
  title?: string;
  subtitle?: string;
  description?: string;
  image?: string;
  link?: string;
};

function asArray<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

function LinkCard({ link, className }: { link: GenUiLink; className?: string }) {
  if (!link?.url) return null;
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noreferrer"
      className={cn(
        "flex items-start gap-2 rounded-md border bg-card px-3 py-2 text-left text-card-foreground transition-colors hover:bg-accent",
        className,
      )}
    >
      <Link2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="min-w-0">
        <span className="block truncate text-xs font-medium">{link.title || link.url}</span>
        {link.snippet ? (
          <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">
            {link.snippet}
          </span>
        ) : null}
      </span>
      <ExternalLink className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
    </a>
  );
}

/** Sources the avatar used (live web search citations). */
function SourcesCard({ data }: { data: Record<string, unknown> }) {
  const title = typeof data.title === "string" ? data.title : "Sources";
  const links = asArray<GenUiLink>(data.links).filter((l) => l?.url);
  if (links.length === 0) return null;
  return (
    <div className="rounded-lg border bg-card/95 p-3 shadow-lg backdrop-blur">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-card-foreground">
        <Search className="h-3.5 w-3.5" />
        {title}
      </p>
      <div className="space-y-1.5">
        {links.slice(0, 5).map((l, i) => (
          <LinkCard key={`${l.url}-${i}`} link={l} />
        ))}
      </div>
    </div>
  );
}

/** Suggested follow-up questions. */
function FollowupsCard({
  data,
  onPick,
}: {
  data: Record<string, unknown>;
  onPick?: (q: string) => void;
}) {
  const title = typeof data.title === "string" ? data.title : "You could ask";
  const questions = asArray<string>(data.questions).filter(
    (q): q is string => typeof q === "string" && !!q.trim(),
  );
  if (questions.length === 0) return null;
  return (
    <div className="rounded-lg border bg-card/95 p-3 shadow-lg backdrop-blur">
      <p className="mb-2 text-xs font-semibold text-card-foreground">{title}</p>
      <div className="flex flex-wrap gap-1.5">
        {questions.slice(0, 4).map((q, i) => (
          <button
            key={`${q}-${i}`}
            type="button"
            onClick={() => onPick?.(q)}
            className="rounded-full border bg-background px-2.5 py-1 text-left text-xs text-foreground transition-colors hover:bg-accent"
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Single link card the agent pushed. */
function ShowLinkCard({ data }: { data: Record<string, unknown> }) {
  const link = (data.link ?? data) as GenUiLink;
  if (!link?.url) return null;
  return (
    <div className="rounded-lg border bg-card/95 p-2 shadow-lg backdrop-blur">
      <LinkCard link={link} />
    </div>
  );
}

/** Overlay for aux widgets (sources / followups / show-link). */
export function GenUiAuxOverlay({
  widget,
  onFollowup,
}: {
  widget: GenUiWidget | null;
  onFollowup?: (q: string) => void;
}) {
  if (!widget) return null;
  if (widget.kind === "sources") return <SourcesCard data={widget.data} />;
  if (widget.kind === "followups") return <FollowupsCard data={widget.data} onPick={onFollowup} />;
  if (widget.kind === "show-link") return <ShowLinkCard data={widget.data} />;
  return null;
}


// Note: there is deliberately no media takeover pane. Agent-pushed media
// widgets are refused in `KalturaAgentFrame` — Kaltura emitted empty
// `content-gallery` payloads that blanked the learner's content area. The avatar
// presents the scenario document through our own client tools instead.
